"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheEcoGrowStory() {
  const {
    ref: topRef,
    isVisible: topVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: bottomRef,
    isVisible: bottomVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const partnerFarms = [{
    name: "Heritage Heirloom Plots",
    region: "North District, City",
    latin: "Solanum lycopersicum",
    english: "Heirloom Tomatoes"
  }, {
    name: "Greenhouse Collective",
    region: "Central Hub, City",
    latin: "Ocimum basilicum",
    english: "Sweet Basil"
  }, {
    name: "Compost & Culture",
    region: "East Side, City",
    latin: "Brassica oleracea",
    english: "Organic Kale"
  }, {
    name: "Seed Savers Union",
    region: "West Valley, City",
    latin: "Cucurbita pepo",
    english: "Summer Squash"
  }];
  const principles = ["100% Organic Practices", "Community Seed Swapping", "Zero-Waste Composting", "Seasonal Crop Rotation", "Shared Tool Library", "Educational Workshops"];
  return <section data-section-id="2034" id="the-eco-grow-story" className="relative bg-background text-foreground py-24 md:py-36 overflow-hidden font-default">
      {}
      <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-lg bg-primary" />
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-primary/40" />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {}
        <div ref={topRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-[720ms] ease-out ${topVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-8">
            {}
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-foreground/60">&nbsp;— OUR COMMUNITY STORY</span>
              <span className="h-px flex-1 bg-foreground/20 max-w-[120px]" />
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-lg bg-primary" />
            </div>

            {}
            <h2 className="font-default font-normal text-foreground leading-[1.02] tracking-tight mb-12" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)"
          }}>
              Growing together,
              <br />
              one <span className="italic text-primary font-light">harvest</span> at a time
            </h2>

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 relative">
              <div className="md:max-w-[32ch]">
                <p className="text-foreground/85 leading-[1.7] text-base">
                  Every seed planted in our community garden begins as a shared
                  vision — with neighbors we have known for years, with
                  volunteers who steward our local soil, and with families who
                  value the rhythm of the seasons.
                </p>
              </div>
              {}
              <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-foreground/20" />
              <div className="md:max-w-[32ch] md:pl-2">
                <p className="text-foreground/85 leading-[1.7] text-base">
                  We cultivate connection, we share our knowledge, and we nurture
                  the land. The result is a vibrant neighborhood space — and
                  bountiful harvests that bring our community closer to the
                  earth we call home.
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-foreground/60">
                Featured Plots
              </span>
              <span className="h-px flex-1 bg-foreground/20" />
            </div>

            <ul className="flex flex-col">
              {partnerFarms.map((farm, i) => <li data-index={i} key={i} className="relative py-5 border-b border-foreground/20 last:border-b-0">
                  {}
                  <span aria-hidden="true" className="absolute -bottom-[3px] right-0 w-1.5 h-1.5 rounded-lg bg-primary last:hidden" style={{
                display: i === partnerFarms.length - 1 ? "none" : undefined
              }} />
                  <div className="font-default font-medium text-foreground text-[15px] leading-tight mb-1.5">
                    {farm.name}
                  </div>
                  <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground/60 mb-2">
                    {farm.region}
                  </div>
                  <div className="font-default italic text-foreground/80 text-sm">
                    {farm.latin}{" "}
                    <span className="not-italic text-foreground/55 text-[13px]">
                      ({farm.english})
                    </span>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>

        {}
        <div className="relative my-20 md:my-28" aria-hidden="true">
          <div className="h-px bg-foreground/20" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-lg bg-primary" />
        </div>

        {}
        <div ref={bottomRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-[720ms] ease-out ${bottomVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-xs tracking-[0.18em] uppercase text-foreground/60">
                Our Principles
              </span>
              <span className="h-px flex-1 bg-foreground/20" />
            </div>

            <ul className="flex flex-col gap-5">
              {principles.map((principle, i) => <li data-index={i} key={i} className="flex items-center gap-4 group">
                  <span aria-hidden="true" className="w-1.5 h-1.5 rounded-lg bg-primary shrink-0" />
                  <span className="font-mono text-xs md:text-[13px] tracking-[0.16em] uppercase text-foreground/90">
                    {principle}
                  </span>
                </li>)}
            </ul>
          </div>

          {}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="relative aspect-[16/9] w-full">
              {}
              <span aria-hidden="true" className="absolute -top-2 -left-2 w-4 h-4 z-10">
                <span className="absolute top-1/2 left-0 right-0 h-px bg-foreground/40" />
                <span className="absolute left-1/2 top-0 bottom-0 w-px bg-foreground/40" />
              </span>
              <span aria-hidden="true" className="absolute -top-2 -right-2 w-4 h-4 z-10">
                <span className="absolute top-1/2 left-0 right-0 h-px bg-foreground/40" />
                <span className="absolute left-1/2 top-0 bottom-0 w-px bg-foreground/40" />
              </span>
              <span aria-hidden="true" className="absolute -bottom-2 -left-2 w-4 h-4 z-10">
                <span className="absolute top-1/2 left-0 right-0 h-px bg-foreground/40" />
                <span className="absolute left-1/2 top-0 bottom-0 w-px bg-foreground/40" />
              </span>
              <span aria-hidden="true" className="absolute -bottom-2 -right-2 w-4 h-4 z-10">
                <span className="absolute top-1/2 left-0 right-0 h-px bg-foreground/40" />
                <span className="absolute left-1/2 top-0 bottom-0 w-px bg-foreground/40" />
              </span>

              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/community_garden_harvest.png-wvc-sizes" alt="A vibrant community garden harvest in the morning light" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

              {}
              <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 bg-background/70 backdrop-blur-sm px-3 py-2">
                <span aria-hidden="true" className="w-1.5 h-1.5 rounded-lg bg-primary" />
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.16em] uppercase text-foreground/90">
                  City Garden / Summer
                </span>
              </div>

              {}
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
                <span className="font-mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase text-foreground/70 bg-background/60 backdrop-blur-sm px-3 py-1.5">
                  45.5231° N / 122.6765° W
                </span>
              </div>
            </div>

            {}
            <div className="mt-6 flex items-start justify-between gap-6">
              <p className="font-default italic text-foreground/70 text-sm leading-relaxed max-w-[42ch]">
                Heirloom tomatoes, nurtured by our local volunteers at the community plot — a project entering its fifth successful season.
              </p>
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/50 shrink-0 pt-1">
                Plot 05
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}